import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowRight,
  Bot,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Clock3,
  Database,
  Eye,
  GitCompareArrows,
  LockKeyhole,
  RefreshCw,
  RotateCcw,
  Scale,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  UserRoundCheck,
  X,
  Zap,
} from "lucide-react";
import ExperienceDemo from "./ExperienceDemo.jsx";
import "./experience-demo.css";

const journeyStages = [
  ["P0", "适配评估"],
  ["P1", "明确问题"],
  ["P2", "补充证据"],
  ["P3", "初步诊断"],
  ["P4", "定位断点"],
  ["P5", "选择行动"],
  ["P6", "上线触点"],
  ["P7", "验证变化"],
  ["P8", "沉淀经验"],
  ["P9", "下一轮增长"],
];

const sceneLabels = {
  now: "当前判断",
  options: "比较方案",
  action: "执行行动",
  observe: "观察变化",
};

const roleLabels = {
  CLIENT_DECISION_OWNER: "客户决策人",
  CLIENT_CONTRIBUTOR: "客户协作者",
  LANJI_REVIEWER: "览极审核人",
  LANJI_ADMIN: "览极管理员",
};

const measurementLabels = {
  DIRECT_TRANSACTION: "真实交易",
  DIRECT_BEHAVIOR: "真实行为",
  SELF_REPORTED: "客户确认",
  PROXY: "代理观察",
  UNMEASURED: "等待测量",
};

const metricRoleLabels = {
  RESULT: "经营结果",
  CHOICE: "客户选择",
  DRIVER: "关键驱动",
  GUARDRAIL: "风险护栏",
};

const outcomeDimensionLabels = {
  volume: ["客户数量", "有效客户、订单、商机、复购或续约数量"],
  efficiency: ["选择效率", "选择率、转化率、响应时间与成交周期"],
  cost: ["选择成本", "CAC、销售人力、折扣与内容成本"],
  quality: ["选择质量", "毛利、退款、投诉、留存与履约风险"],
};

const outcomeStatusLabels = {
  MEASURABLE_AFTER_VERIFICATION: "可验证量化",
  PARTIAL_MAPPING: "部分可观察",
  NOT_YET_MEASURED: "需要补数",
};

async function api(path, options = {}) {
  const response = await fetch(path, {
    ...options,
    headers: { ...(options.body ? { "content-type": "application/json" } : {}), ...options.headers },
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error?.message ?? "请求失败");
  return json.data;
}

function useWorkspace() {
  const [session, setSession] = useState(null);
  const [pkg, setPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = async () => {
    try {
      setError("");
      const [nextSession, nextPackage] = await Promise.all([api("/api/session"), api("/api/package")]);
      setSession(nextSession);
      setPackage(nextPackage);
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);
  return { session, pkg, loading, error, refresh, setSession };
}

export default function App() {
  const inviteToken = new URLSearchParams(window.location.search).get("token");
  if (window.location.pathname === "/demo") return <ExperienceDemo />;
  if (window.location.pathname === "/invite" && inviteToken) return <InviteRedeemer token={inviteToken} />;
  return <Workspace />;
}

function InviteRedeemer({ token }) {
  const [state, setState] = useState("redeeming");
  const [message, setMessage] = useState("正在验证一次性邀请链接");
  useEffect(() => {
    api("/api/invites/redeem", { method: "POST", body: JSON.stringify({ token }) })
      .then(() => {
        setState("done");
        setMessage("验证完成，正在进入览极 AI 增长空间");
        setTimeout(() => { window.location.replace("/"); }, 500);
      })
      .catch((error) => { setState("error"); setMessage(error.message); });
  }, [token]);
  return (
    <main className="center-state">
      <Brand />
      {state === "error" ? <CircleAlert size={30} /> : state === "done" ? <CircleCheck size={30} /> : <span className="spinner" />}
      <h1>{state === "error" ? "无法进入增长空间" : "正在安全进入览极 AI"}</h1>
      <p>{message}</p>
    </main>
  );
}

function Workspace() {
  const { session, pkg, loading, error, refresh, setSession } = useWorkspace();
  const [scene, setScene] = useState("now");
  const [selectionOpen, setSelectionOpen] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [mutationError, setMutationError] = useState("");

  const mutate = async (path, body, success, nextScene) => {
    try {
      setMutationError("");
      await api(path, { method: "POST", body: JSON.stringify(body) });
      setNotice(success);
      await refresh();
      if (nextScene) setScene(nextScene);
      setTimeout(() => setNotice(""), 3200);
    } catch (nextError) {
      setMutationError(nextError.message);
      throw nextError;
    }
  };

  if (loading) return <LoadingState />;
  if (error || !pkg || !session) return <ErrorState message={error} onRetry={refresh} />;

  const switchRole = async (role) => {
    const next = await api("/api/session/role", { method: "POST", body: JSON.stringify({ role }) });
    setSession(next);
    await refresh();
    setScene("now");
  };

  const reviewer = session.role === "LANJI_REVIEWER" || session.role === "LANJI_ADMIN";
  return (
    <div className="product-shell">
      <ProductHeader pkg={pkg} session={session} onRoleChange={switchRole} reviewer={reviewer} />
      {pkg.demo_mode && <DemoNotice />}
      {reviewer ? (
        <ReviewerWorkspace pkg={pkg} />
      ) : (
        <ClientWorkspace
          pkg={pkg}
          scene={scene}
          onScene={setScene}
          onSelect={() => setSelectionOpen(true)}
          onRevision={() => setRevisionOpen(true)}
          mutate={mutate}
        />
      )}
      {selectionOpen && (
        <SelectionDialog
          pkg={pkg}
          onClose={() => setSelectionOpen(false)}
          onSubmit={(body) => mutate("/api/decision", body, "你的选择已记录。览极 AI 已把下一步收敛为行动确认。", "action").then(() => setSelectionOpen(false))}
        />
      )}
      {revisionOpen && (
        <RevisionDialog
          pkg={pkg}
          onClose={() => setRevisionOpen(false)}
          onSubmit={(body) => mutate("/api/revision-requests", body, "你的纠正已进入审核，旧判断不会被覆盖。", "now").then(() => setRevisionOpen(false))}
        />
      )}
      {notice && <div className="toast"><CheckCircle2 size={17} />{notice}</div>}
      {mutationError && <div className="error-toast"><CircleAlert size={17} />{mutationError}<button onClick={() => setMutationError("")} aria-label="关闭"><X size={15} /></button></div>}
    </div>
  );
}

function ProductHeader({ pkg, session, onRoleChange, reviewer }) {
  return (
    <header className="product-header">
      <Brand />
      <div className="project-identity">
        <span>{pkg.company.company_name}</span>
        <ChevronRight size={14} />
        <strong>{reviewer ? "审核与治理" : "增长决策空间"}</strong>
      </div>
      <div className="header-actions">
        <span className="ai-online"><i />览极 AI 在线</span>
        {session.demo_mode && (
          <label className="role-switch">
            <span>体验身份</span>
            <select value={session.role} onChange={(event) => onRoleChange(event.target.value)}>
              <option value="CLIENT_DECISION_OWNER">客户决策人</option>
              <option value="CLIENT_CONTRIBUTOR">客户协作者</option>
              <option value="LANJI_REVIEWER">览极审核人</option>
            </select>
            <ChevronDown size={14} />
          </label>
        )}
      </div>
    </header>
  );
}

function DemoNotice() {
  return <div className="demo-notice"><Database size={14} /><span>当前为合成演示数据，用于体验产品流程，不代表任何真实企业结论。</span></div>;
}

function Brand() {
  return <div className="brand" aria-label="览极 AI 系统"><span>览极</span><i /><strong>AI 系统</strong></div>;
}

function LoadingState() {
  return <main className="center-state"><Brand /><span className="spinner" /><h1>览极 AI 正在恢复本轮上下文</h1><p>同步问题、证据、方案与行动状态</p></main>;
}

function ErrorState({ message, onRetry }) {
  return <main className="center-state"><Brand /><CircleAlert size={30} /><h1>暂时无法载入增长空间</h1><p>{message || "请确认邀请链接或服务状态"}</p><button className="button primary" onClick={onRetry}><RotateCcw size={17} />重新载入</button></main>;
}

function ClientWorkspace({ pkg, scene, onScene, onSelect, onRevision, mutate }) {
  const activeStage = stageForPackage(pkg);
  return (
    <>
      <ExperienceRail activeStage={activeStage} />
      <div className="client-frame">
        <main className="journey-workspace">
          <JourneyTabs scene={scene} pkg={pkg} onScene={onScene} />
          {scene === "now" && <NowScene pkg={pkg} onScene={onScene} onRevision={onRevision} />}
          {scene === "options" && <OptionsScene pkg={pkg} onSelect={onSelect} onRevision={onRevision} />}
          {scene === "action" && <ActionScene pkg={pkg} mutate={mutate} onScene={onScene} />}
          {scene === "observe" && <ObserveScene pkg={pkg} mutate={mutate} />}
        </main>
        <AiCollaborator pkg={pkg} scene={scene} onScene={onScene} />
      </div>
    </>
  );
}

function ExperienceRail({ activeStage }) {
  const currentIndex = journeyStages.findIndex(([code]) => code === activeStage);
  return (
    <div className="journey-rail" aria-label="增长任务进度">
      <div className="journey-rail-inner">
        {journeyStages.map(([code, label], index) => (
          <div key={code} className={`${index < currentIndex ? "complete" : ""} ${index === currentIndex ? "active" : ""}`}>
            <span>{index < currentIndex ? <Check size={12} /> : index + 1}</span>
            <p>{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function JourneyTabs({ scene, pkg, onScene }) {
  const unlocked = {
    now: true,
    options: Boolean(pkg.options?.items?.length),
    action: Boolean(pkg.decision || pkg.options?.items?.length),
    observe: Boolean(pkg.metrics?.contracts?.length),
  };
  return (
    <nav className="journey-tabs" aria-label="当前增长任务视图">
      {Object.entries(sceneLabels).map(([key, label]) => (
        <button key={key} className={scene === key ? "active" : ""} disabled={!unlocked[key]} onClick={() => onScene(key)}>
          {label}
        </button>
      ))}
    </nav>
  );
}

function NowScene({ pkg, onScene, onRevision }) {
  const provisional = Boolean(pkg.commercial_context?.provisional_only);
  const conclusion = pkg.diagnosis.conclusions?.[0]?.conclusion_text ?? pkg.diagnosis.choice_scenario;
  const receipt = pkg.metrics.quantification_receipt;
  const activeModules = new Set(provisional ? [] : ["市场", "营销", "销售"]);
  return (
    <div className="scene-stack">
      {provisional && <section className="provisional-notice"><ShieldCheck size={18} /><div><strong>测试项目工作区</strong><p>套餐选择已记录，但没有扣款，也没有自动放行诊断结论。证据与人工复核通过后，项目才会升级。</p></div></section>}
      <section className="ai-conclusion-band">
        <div className="ai-mark"><Sparkles size={18} /><span>览极 AI 首轮判断</span></div>
        <div className="conclusion-layout">
          <div>
            <p className="stage-context">{provisional ? "已完成问题梳理，正在确认增长断点与关键证据" : "已经完成问题梳理、证据检查与断点定位"}</p>
            <h1>{conclusion}</h1>
            <p className="conclusion-copy">{provisional ? "这是免费首检形成的待复核假设。先确认增长目标与关键证据，方案比较将在审核后开放。" : "这不是最终结论。它是当前证据下最值得先验证的增长方向，下一步由你决定是否进入方案比较。"}</p>
          </div>
          <div className="confidence-block">
            <span>当前判断可信度</span>
            <strong>{provisional ? "待复核" : "中"}</strong>
            <p>{provisional ? `${receipt?.data_level ?? "L0"} 自报信息 · 尚未放行` : `${receipt?.data_level ?? "L0"} 数据支持 · 仍需真实变化验证`}</p>
          </div>
        </div>
        <div className="primary-actions">
          <button className="button primary" disabled={provisional} onClick={() => onScene("options")}>{provisional ? "等待证据复核" : "比较可执行方案"}{!provisional && <ArrowRight size={17} />}</button>
          <button className="button secondary" onClick={onRevision}>这个判断不符合实际</button>
        </div>
      </section>

      <section className="outcome-workspace">
        <SectionHeading eyebrow="本轮增长目标" title={pkg.growth_objective?.label ?? "增长目标待确认"} detail="览极不把四个维度压成一个总分；每个方向分别绑定证据、指标和验证状态。" />
        <div className="outcome-workspace-grid">
          {Object.entries(outcomeDimensionLabels).map(([key, [label, detail]]) => {
            const item = pkg.choice_outcome_vector?.[key] ?? {};
            return <article className={item.priority ? "priority" : ""} key={key}><span>{label}</span><strong>{item.priority ? "本轮优先" : outcomeStatusLabels[item.measurement_status] ?? "待建立映射"}</strong><p>{detail}</p><small>{outcomeStatusLabels[item.measurement_status] ?? "待建立映射"}</small></article>;
          })}
        </div>
      </section>

      <section className="reasoning-section">
        <SectionHeading eyebrow="AI 如何理解" title="从客户当前状态，到最值得改变的环节" detail="览极把经营问题转换成一条可以观察和验证的客户选择路径。" />
        <div className="choice-journey">
          <div><small>客户当前状态</small><strong>{pkg.diagnosis.current_choice_state}</strong><p>客户已经产生认知，但尚未稳定进入下一步。</p></div>
          <ArrowRight size={20} />
          <div className="breakpoint"><small>优先验证的阻力</small><strong>{pkg.diagnosis.primary_breakpoint}</strong><p>{pkg.diagnosis.choice_scenario}</p></div>
          <ArrowRight size={20} />
          <div><small>希望推动到</small><strong>{pkg.diagnosis.target_choice_state}</strong><p>让更匹配的客户完成可观察的选择行为。</p></div>
        </div>
      </section>

      <section className="module-section">
        <SectionHeading eyebrow="本轮分析范围" title={provisional ? "分析范围将在证据复核后确认" : "AI 已把跨部门问题收敛到三个重点业务面"} detail={provisional ? "当前只记录客户自报的增长目标，不提前假定问题属于某个部门。" : "其他模块仍保留在整体诊断中，但不会同时占用你的注意力。"} />
        <div className="module-map">
          {["战略", "市场", "产品", "营销", "销售", "组织", "数字化", "报告"].map((module) => (
            <span className={activeModules.has(module) ? "active" : ""} key={module}>{activeModules.has(module) && <Zap size={13} />}{module}</span>
          ))}
        </div>
      </section>

      <EvidenceDisclosure pkg={pkg} />
    </div>
  );
}

function EvidenceDisclosure({ pkg }) {
  return (
    <details className="evidence-disclosure">
      <summary><span><ShieldCheck size={17} />查看判断依据与边界</span><ChevronDown size={17} /></summary>
      <div className="evidence-body">
        <div><strong>当前可以用于</strong>{pkg.diagnosis.allowed_uses.map((item) => <p key={item}><CircleCheck size={15} />{item}</p>)}</div>
        <div><strong>现在不能推出</strong>{pkg.diagnosis.forbidden_uses.map((item) => <p key={item}><CircleAlert size={15} />{item}</p>)}</div>
        <div className="weakest"><strong>最可能出错的位置</strong><p>{pkg.diagnosis.weakest_assumption}</p></div>
      </div>
    </details>
  );
}

function OptionsScene({ pkg, onSelect, onRevision }) {
  const options = pkg.options?.items ?? [];
  if (!options.length) return <EmptyState icon={GitCompareArrows} title="方案正在生成" detail="览极 AI 会提供至少两个可行动方案和一个不行动基线。" />;
  return (
    <div className="scene-stack">
      <section className="scene-intro">
        <div className="ai-mark"><GitCompareArrows size={18} /><span>当前只需要完成一项选择</span></div>
        <h1>{pkg.options.set.decision_question}</h1>
        <p>我已把方案放进相同的预算、人员和验证条件中。推荐项只代表当前证据更支持，不替你作决定。</p>
        <div className="constraint-strip">
          <span><small>本轮优先</small><strong>{translatePriority(pkg.options.set.priority_dimension)}</strong></span>
          <span><small>预算边界</small><strong>{money(pkg.options.set.budget_cap_amount, pkg.options.set.currency)}</strong></span>
          <span><small>可投入人员</small><strong>{pkg.options.set.people_cap} 人以内</strong></span>
          <span><small>验证条件</small><strong>{translateRobustness(pkg.options.set.robustness_status)}</strong></span>
        </div>
      </section>

      <section className="option-list" aria-label="增长方案">
        {options.map((option) => (
          <OptionCard
            key={option.decision_option_id}
            option={option}
            recommended={pkg.options.set.recommended_option_id === option.decision_option_id}
            selected={pkg.decision?.selected_option_id === option.decision_option_id}
          />
        ))}
      </section>

      <div className="decision-footer">
        <div><strong>选择时请优先判断</strong><p>企业是否能投入相应资源，以及你是否愿意接受该方案的约束。</p></div>
        <div className="primary-actions">
          {pkg.permissions.can_request_revision && <button className="button secondary" onClick={onRevision}>条件不准确</button>}
          {!pkg.decision && pkg.permissions.can_select && <button className="button primary" onClick={onSelect}>记录我的选择<ArrowRight size={17} /></button>}
        </div>
      </div>
    </div>
  );
}

function OptionCard({ option, recommended, selected }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <article className={`option-card ${recommended ? "recommended" : ""} ${selected ? "selected" : ""}`}>
      <header>
        <div className="option-labels">
          <span>{translateOptionType(option.option_type)}</span>
          {recommended && <b><Sparkles size={13} />AI 建议优先比较</b>}
          {selected && <b className="chosen"><Check size={13} />你已选择</b>}
        </div>
        <h2>{option.title}</h2>
        <p>{option.expected_effect}</p>
      </header>
      <div className="option-facts">
        <span><small>预算</small><strong>{money(option.required_budget_amount, option.currency)}</strong></span>
        <span><small>人员</small><strong>{option.people_required} 人</strong></span>
        <span><small>验证周期</small><strong>{option.duration_days} 天</strong></span>
      </div>
      <div className="option-tradeoff"><Scale size={16} /><p><strong>需要接受：</strong>{option.accepted_constraints.join("；")}</p></div>
      <button className="detail-toggle" onClick={() => setExpanded(!expanded)}>{expanded ? "收起依据" : "查看风险与停止条件"}<ChevronDown size={16} /></button>
      {expanded && <div className="option-details"><p><strong>主要风险</strong>{option.risks.join("；")}</p><p><strong>停止条件</strong>{option.stop_condition}</p><p><strong>什么会推翻它</strong>{option.falsification_signal}</p></div>}
    </article>
  );
}

function ActionScene({ pkg, mutate, onScene }) {
  const commitment = pkg.action?.commitment;
  const selected = pkg.options?.items?.find((item) => item.decision_option_id === pkg.decision?.selected_option_id);
  const [mode, setMode] = useState("ACCEPTED_AS_IS");
  const [reason, setReason] = useState("");
  const [owner, setOwner] = useState("增长项目负责人");
  const [metric, setMetric] = useState(pkg.metrics.contracts[0]?.metric_id ?? "");
  const [startTrigger, setStartTrigger] = useState("方案素材、落地页与测量口径均准备完成时");
  const [reviewTrigger, setReviewTrigger] = useState("行动上线、关键指标显著变化或证据发生冲突时");
  const [eventType, setEventType] = useState("STARTED");
  const [eventSummary, setEventSummary] = useState("");

  if (!pkg.decision) return <EmptyState icon={Target} title="请先完成方案选择" detail="行动只能建立在客户明确作出的选择上。" action="返回比较方案" onAction={() => onScene("options")} />;

  const submitCommitment = (event) => {
    event.preventDefault();
    mutate("/api/action", {
      decision_state: mode,
      decision_reason: reason,
      committed_owner_id: mode === "ACCEPTED_AS_IS" ? owner : null,
      metric_id: mode === "ACCEPTED_AS_IS" ? metric : null,
      start_trigger: mode === "ACCEPTED_AS_IS" ? startTrigger : null,
      next_review_trigger: reviewTrigger,
    }, "行动决定已记录。只有真实执行发生后，览极 AI 才会进入观察。", "action");
  };

  const submitEvent = (event) => {
    event.preventDefault();
    mutate("/api/action-events", {
      event_type: eventType,
      actual_change_summary: eventSummary,
      evidence_status: "SELF_REPORTED",
      evidence_refs: [],
    }, "执行事实已记录。览极 AI 将等待数据或事件触发复核。", "observe").then(() => setEventSummary(""));
  };

  return (
    <div className="scene-stack">
      <section className="scene-intro compact">
        <div className="ai-mark"><Target size={18} /><span>把选择变成真实改变</span></div>
        <h1>{commitment ? "记录实际发生了什么" : "确认谁在什么条件下开始行动"}</h1>
        <p>览极会严格区分“选择了方案”“执行了行动”和“行动产生效果”。</p>
      </section>

      <section className="selected-action">
        <div><small>你选择的路径</small><h2>{selected?.title ?? "已选方案"}</h2><p>{pkg.action.card.action_description}</p></div>
        <span><small>停止条件</small><strong>{pkg.action.card.stop_condition}</strong></span>
      </section>

      {!commitment ? (
        <form className="action-form" onSubmit={submitCommitment}>
          <fieldset>
            <legend>你准备如何处理这项行动？</legend>
            <div className="choice-buttons">
              {[["ACCEPTED_AS_IS", "采纳并准备执行"], ["REVISION_REQUESTED", "需要修改"], ["DEFERRED", "暂时延后"], ["REJECTED", "不采用"]].map(([value, label]) => (
                <button type="button" key={value} className={mode === value ? "active" : ""} onClick={() => setMode(value)}>{mode === value && <Check size={14} />}{label}</button>
              ))}
            </div>
          </fieldset>
          <label className="field"><span>作出这个决定的原因</span><textarea value={reason} onChange={(event) => setReason(event.target.value)} required placeholder="例如：资源可以投入，但需要先限制渠道范围" /></label>
          {mode === "ACCEPTED_AS_IS" && <div className="field-grid"><label className="field"><span>负责人</span><input value={owner} onChange={(event) => setOwner(event.target.value)} required /></label><label className="field"><span>主要观察指标</span><select value={metric} onChange={(event) => setMetric(event.target.value)}>{pkg.metrics.contracts.map((item) => <option key={item.metric_id} value={item.metric_id}>{item.metric_name}</option>)}</select></label><label className="field full"><span>什么准备好以后开始</span><input value={startTrigger} onChange={(event) => setStartTrigger(event.target.value)} required /></label></div>}
          <label className="field"><span>发生什么时需要重新判断</span><input value={reviewTrigger} onChange={(event) => setReviewTrigger(event.target.value)} required /><small>使用真实事件，不使用固定 30/60/90 天。</small></label>
          <div className="form-actions"><button className="button primary" type="submit">确认行动决定<ArrowRight size={17} /></button></div>
        </form>
      ) : (
        <div className="action-runtime">
          <section className="commitment-summary">
            <SectionHeading eyebrow="已确认" title={translateDecisionState(commitment.decision_state)} />
            <dl><div><dt>负责人</dt><dd>{commitment.committed_owner_id ?? "未绑定"}</dd></div><div><dt>启动条件</dt><dd>{commitment.start_trigger ?? "不适用"}</dd></div><div><dt>下次触发</dt><dd>{commitment.next_review_trigger}</dd></div></dl>
          </section>
          {commitment.decision_state === "ACCEPTED_AS_IS" && <form className="execution-form" onSubmit={submitEvent}><SectionHeading eyebrow="执行事实" title="刚刚发生了什么？" /><label className="field"><span>状态变化</span><select value={eventType} onChange={(event) => setEventType(event.target.value)}>{[["STARTED", "行动已开始"], ["PAUSED", "暂时暂停"], ["RESUMED", "恢复执行"], ["COMPLETED", "完成执行"], ["STOPPED", "停止行动"], ["SCOPE_CHANGED", "范围发生变化"]].map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><label className="field"><span>实际改变了什么</span><textarea value={eventSummary} onChange={(event) => setEventSummary(event.target.value)} required placeholder="只记录实际发生的变化，不提前判断效果" /></label><button className="button primary" type="submit">记录真实变化<ArrowRight size={17} /></button></form>}
        </div>
      )}
      {pkg.action?.events?.length > 0 && <ExecutionTimeline events={pkg.action.events} />}
    </div>
  );
}

function ExecutionTimeline({ events }) {
  return <section className="execution-timeline"><SectionHeading eyebrow="执行轨迹" title="已经发生的真实变化" />{events.map((event) => <div key={event.action_execution_event_id}><span /><p><strong>{translateEvent(event.event_type)}</strong>{event.actual_change_summary}<small>{new Date(event.event_time).toLocaleString("zh-CN")} · 客户确认</small></p></div>)}</section>;
}

function ObserveScene({ pkg, mutate }) {
  const receipt = pkg.metrics.quantification_receipt;
  const openChecks = pkg.progress_checks.filter((check) => check.status === "OPEN");
  return (
    <div className="scene-stack">
      <section className="scene-intro">
        <div className="ai-mark"><Activity size={18} /><span>增长观察</span></div>
        <h1>{openChecks.length ? "有一项真实变化需要你确认" : "览极 AI 正在等待足以改变判断的信号"}</h1>
        <p>这里不按固定日期要求复盘。只有数据、证据或行动状态发生有意义的变化，AI 才会发起确认。</p>
      </section>
      <section className="metric-story">
        <SectionHeading eyebrow="本轮指标路径" title="经营结果、客户选择、关键驱动和风险护栏" detail="每项判断都有指标归宿，但指标存在不代表增长已经发生。" />
        <div className="metric-signals">
          {pkg.metrics.contracts.map((metric) => <MetricSignal key={metric.metric_id} metric={metric} demo={pkg.demo_mode} />)}
        </div>
        <div className="measurement-status"><ShieldCheck size={16} /><span>当前可测量状态</span><strong>{translateQuantification(receipt?.quantification_status)}</strong><p>{receipt?.missing_data?.[0] ?? "等待新的可比数据"}</p></div>
      </section>
      {openChecks.map((check) => <ProgressCheck key={check.progress_check_id} check={check} onSubmit={(body) => mutate(`/api/progress-checks/${check.progress_check_id}/respond`, body, "这次变化已提交，览极 AI 将重新评估当前判断。", "now")} />)}
      {!openChecks.length && <section className="quiet-observation"><Eye size={22} /><div><h2>目前不需要你填写任何内容</h2><p>继续执行即可。当指标显著变化、证据冲突、行动状态变化或你主动请求时，览极才会打扰你。</p></div></section>}
      <EvidenceDisclosure pkg={pkg} />
    </div>
  );
}

function MetricSignal({ metric, demo }) {
  const isRate = /RATE|MARGIN/u.test(metric.metric_code ?? "");
  const value = metric.latest_value_numeric == null ? "等待观察" : `${metric.latest_value_numeric}${isRate ? "%" : ""}`;
  return (
    <div className={`metric-signal role-${metric.quantification_role?.toLowerCase()}`}>
      <span>{metricRoleLabels[metric.quantification_role] ?? "诊断指标"}</span>
      <h3>{metric.metric_name}</h3>
      <strong>{value}</strong>
      <p>{measurementLabels[metric.measurement_type] ?? "测量方式待确认"}{demo && metric.latest_value_numeric != null ? " · 合成演示值" : ""}</p>
    </div>
  );
}

function ProgressCheck({ check, onSubmit }) {
  const [answers, setAnswers] = useState({});
  const submit = (event) => {
    event.preventDefault();
    onSubmit({
      status: "ANSWERED",
      response_mode: "short_text",
      response_summary: answers,
      attachment_refs: [],
      judgment_change: "已收到客户对真实变化的确认，等待证据重新评估",
      recommended_decision: "SUPPLY_EVIDENCE",
      next_minimum_evidence_action: "将本次回答与行为或交易证据交叉核验",
      next_trigger: "出现可比指标变化或证据冲突解除时",
    });
  };
  return (
    <form className="progress-check" onSubmit={submit}>
      <header><div><span>{translateTrigger(check.trigger_type)}</span><h2>这项变化可能影响当前判断</h2><p>览极复用了已有信息，只需要你确认以下 {check.questions.length} 项。</p></div><RefreshCw size={22} /></header>
      {check.questions.map((question, index) => <label className="field" key={question.question_id}><span>{index + 1}. {question.prompt}</span>{question.options?.length ? <div className="choice-buttons compact">{question.options.map((option) => <button type="button" key={option} className={answers[question.question_id] === option ? "active" : ""} onClick={() => setAnswers({ ...answers, [question.question_id]: option })}>{option}</button>)}</div> : <input value={answers[question.question_id] ?? ""} onChange={(event) => setAnswers({ ...answers, [question.question_id]: event.target.value })} />}</label>)}
      <div className="form-actions"><button className="button secondary" type="button" onClick={() => onSubmit({ status: "SKIPPED", response_mode: "short_text", response_summary: null, attachment_refs: [], judgment_change: "客户本次跳过，当前判断不升级", recommended_decision: "NO_CHANGE", next_minimum_evidence_action: "等待新的系统证据", next_trigger: "出现新的行动或指标变化时" })}>暂时跳过</button><button className="button primary" type="submit">提交并重新判断<ArrowRight size={17} /></button></div>
    </form>
  );
}

function AiCollaborator({ pkg, scene, onScene }) {
  const initial = useMemo(() => aiOpening(pkg, scene), [pkg, scene]);
  const provisional = Boolean(pkg.commercial_context?.provisional_only);
  const initialBoundary = provisional ? "基于客户自报信息，尚未放行" : "基于当前已放行信息";
  const [messages, setMessages] = useState([{ role: "assistant", text: initial, boundary: initialBoundary }]);
  const [value, setValue] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { setMessages([{ role: "assistant", text: initial, boundary: initialBoundary }]); }, [initial, initialBoundary]);

  const send = async (text = value) => {
    const question = text.trim();
    if (!question || sending) return;
    setMessages((current) => [...current, { role: "user", text: question }]);
    setValue("");
    setSending(true);
    try {
      const response = await api("/api/assistant", { method: "POST", body: JSON.stringify({ question }) });
      setMessages((current) => [...current, { role: "assistant", text: response.answer, boundary: response.boundary_notice, command: response.proposed_command }]);
    } catch (error) {
      setMessages((current) => [...current, { role: "assistant", text: error.message, boundary: "本次没有改变当前判断" }]);
    } finally {
      setSending(false);
    }
  };

  const followCommand = (command) => {
    const map = { OPEN_METRICS: "observe", OPEN_METRIC_CONTRACTS: "observe", OPEN_ACTION: "action", OPEN_ACTION_DECISION: "action", OPEN_OPTION_COMPARISON: "options", OPEN_CURRENT_TASK: "now" };
    onScene(map[command.type] ?? "now");
  };

  const prompts = provisional
    ? ["还缺哪项关键证据？", "为什么现在不能比较方案？", "我可以先确认什么？"]
    : scene === "options" ? ["为什么优先这个方向？", "三个方案最大的差别？", "哪个风险最容易忽略？"] : scene === "observe" ? ["现在能判断增长吗？", "还缺哪项数据？", "什么变化会触发复核？"] : ["为什么是这个断点？", "这个判断可能错在哪里？", "接下来我只需做什么？"];

  return (
    <aside className="ai-collaborator">
      <header><span><Bot size={19} /></span><div><strong>览极 AI</strong><p>与你一起完成当前一步</p></div><i /></header>
      <div className="ai-context"><Sparkles size={15} /><p><strong>当前协作任务</strong>{sceneLabels[scene]}</p></div>
      <div className="conversation">
        {messages.map((message, index) => <div className={`message ${message.role}`} key={`${message.role}-${index}`}><p>{message.text}</p>{message.boundary && <small><ShieldCheck size={12} />{message.boundary}</small>}{message.command && <button onClick={() => followCommand(message.command)}>前往相关步骤<ArrowRight size={13} /></button>}</div>)}
        {sending && <div className="message assistant typing"><span /><span /><span /></div>}
      </div>
      <div className="prompt-chips">{prompts.map((prompt) => <button key={prompt} onClick={() => send(prompt)}>{prompt}</button>)}</div>
      <form className="assistant-input" onSubmit={(event) => { event.preventDefault(); send(); }}><textarea value={value} onChange={(event) => setValue(event.target.value)} placeholder="直接纠正、追问或说明企业实际情况" /><button type="submit" aria-label="发送" disabled={!value.trim() || sending}><Send size={17} /></button></form>
      <p className="ai-boundary"><LockKeyhole size={13} />AI 不替你选择，也不会把情景写成增长事实</p>
    </aside>
  );
}

function ReviewerWorkspace({ pkg }) {
  const checks = [
    ["证据快照已绑定", Boolean(pkg.diagnosis.evidence_snapshot_hash)],
    ["最脆弱前提已披露", Boolean(pkg.diagnosis.weakest_assumption)],
    ["禁止用途已声明", pkg.diagnosis.forbidden_uses.length > 0],
    ["增长与客户选择判断已有指标归宿", pkg.metrics.quantification_receipt?.coverage_rate === 1],
  ];
  return (
    <main className="reviewer-workspace">
      <section className="reviewer-heading"><div><span><UserRoundCheck size={18} />览极审核端</span><h1>确认哪些内容可以进入客户体验</h1><p>这里保留完整治理信息，不与客户侧的增长任务界面混用。</p></div><b>{pkg.diagnosis.release_status}</b></section>
      <div className="review-grid">
        <section><SectionHeading eyebrow="客户可见放行" title="四项必要检查" />{checks.map(([label, passed]) => <div className="review-row" key={label}><span className={passed ? "pass" : "wait"}>{passed ? <Check size={14} /> : <Clock3 size={14} />}</span><p>{label}</p><strong>{passed ? "通过" : "待处理"}</strong></div>)}</section>
        <section><SectionHeading eyebrow="内部判断边界" title="不得自动升级" /><p className="review-copy">结论上限：{translateCeiling(pkg.diagnosis.conclusion_ceiling)}</p>{pkg.diagnosis.forbidden_uses.map((item) => <p className="review-warning" key={item}><CircleAlert size={15} />{item}</p>)}</section>
        <section className="wide"><SectionHeading eyebrow="最脆弱前提" title="当前判断最可能在哪里失效" /><p className="large-copy">{pkg.diagnosis.weakest_assumption}</p><small>证据快照：{pkg.diagnosis.evidence_snapshot_hash}</small></section>
      </div>
    </main>
  );
}

function SelectionDialog({ pkg, onClose, onSubmit }) {
  const options = pkg.options.items;
  const [selectedId, setSelectedId] = useState(pkg.options.set.recommended_option_id);
  const [reason, setReason] = useState("");
  const [reasons, setReasons] = useState({});
  const selected = options.find((item) => item.decision_option_id === selectedId);
  const others = options.filter((item) => item.decision_option_id !== selectedId);
  const valid = reason.trim() && others.every((item) => reasons[item.decision_option_id]?.trim());
  const submit = (event) => { event.preventDefault(); onSubmit({ selected_option_id: selectedId, selection_reason: reason, reasons_not_selected: reasons, accepted_constraints: selected.accepted_constraints }); };
  return <Modal title="确认你的增长路径" subtitle="览极会保留选择理由、未选原因与接受的约束。" onClose={onClose}><form onSubmit={submit}><div className="dialog-options">{options.map((option) => <button type="button" key={option.decision_option_id} className={selectedId === option.decision_option_id ? "active" : ""} onClick={() => setSelectedId(option.decision_option_id)}><span>{selectedId === option.decision_option_id && <Check size={14} />}</span><div><strong>{option.title}</strong><small>{money(option.required_budget_amount, option.currency)} · {option.people_required} 人 · {option.duration_days} 天</small></div></button>)}</div><label className="field"><span>为什么选择“{selected.title}”</span><textarea value={reason} onChange={(event) => setReason(event.target.value)} required /></label><div className="not-selected"><strong>为什么没有选择其他路径</strong>{others.map((option) => <label className="field" key={option.decision_option_id}><span>{option.title}</span><input value={reasons[option.decision_option_id] ?? ""} onChange={(event) => setReasons({ ...reasons, [option.decision_option_id]: event.target.value })} required /></label>)}</div><div className="accepted-constraints"><span>确认后同时接受</span>{selected.accepted_constraints.map((item) => <p key={item}><Check size={14} />{item}</p>)}</div><div className="modal-actions"><button type="button" className="button secondary" onClick={onClose}>返回比较</button><button type="submit" className="button primary" disabled={!valid}>确认选择<ArrowRight size={17} /></button></div></form></Modal>;
}

function RevisionDialog({ pkg, onClose, onSubmit }) {
  const [summary, setSummary] = useState("");
  const [priority, setPriority] = useState(pkg.options?.set.priority_dimension ?? "QUALITY");
  const [budget, setBudget] = useState(pkg.options?.set.budget_cap_amount ?? 0);
  return <Modal title="纠正览极 AI 的理解" subtitle="你的纠正会进入审核并生成新版本，不会覆盖原有判断。" onClose={onClose}><form onSubmit={(event) => { event.preventDefault(); onSubmit({ decision_option_set_id: pkg.options?.set.decision_option_set_id ?? null, request_summary: summary, requested_constraints: { priority_dimension: priority, budget_cap_amount: Number(budget) } }); }}><label className="field"><span>哪里不符合企业实际情况</span><textarea value={summary} onChange={(event) => setSummary(event.target.value)} placeholder="例如：当前不是流量质量问题，而是产品交付能力限制了转化" required /></label><div className="field-grid"><label className="field"><span>企业当前更优先</span><select value={priority} onChange={(event) => setPriority(event.target.value)}><option value="QUALITY">客户选择质量</option><option value="EFFICIENCY">客户选择效率</option><option value="COST">客户选择成本</option></select></label><label className="field"><span>实际预算边界</span><input type="number" min="0" value={budget} onChange={(event) => setBudget(event.target.value)} /></label></div><div className="modal-actions"><button type="button" className="button secondary" onClick={onClose}>取消</button><button type="submit" className="button primary" disabled={!summary.trim()}>提交纠正<ArrowRight size={17} /></button></div></form></Modal>;
}

function Modal({ title, subtitle, onClose, children }) {
  return <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><div className="modal" role="dialog" aria-modal="true" aria-label={title}><header><div><h2>{title}</h2><p>{subtitle}</p></div><button className="icon-button" onClick={onClose} aria-label="关闭"><X size={18} /></button></header>{children}</div></div>;
}

function SectionHeading({ eyebrow, title, detail }) {
  return <div className="section-heading"><span>{eyebrow}</span><h2>{title}</h2>{detail && <p>{detail}</p>}</div>;
}

function EmptyState({ icon: Icon, title, detail, action, onAction }) {
  return <section className="empty-state"><span><Icon size={23} /></span><h1>{title}</h1><p>{detail}</p>{action && <button className="button primary" onClick={onAction}>{action}<ArrowRight size={17} /></button>}</section>;
}

function stageForPackage(pkg) {
  if (["ACTION_READY", "EXECUTING", "AWAITING_ACTION_CONFIRMATION"].includes(pkg.package_status)) return "P6";
  if (["OBSERVING", "REVIEW_REQUIRED", "STOPPED", "JUDGMENT_COMPLETED"].includes(pkg.package_status)) return "P7";
  if (pkg.package_status === "AWAITING_SELECTION") return "P5";
  return pkg.project?.stage ?? "P3";
}

function aiOpening(pkg, scene) {
  if (pkg.commercial_context?.provisional_only) return "已记录你的增长目标和首轮损耗假设，但当前仅为客户自报信息。先确认关键证据，方案比较和正式判断尚未开放。";
  if (scene === "options") return `我把“${pkg.diagnosis.primary_breakpoint}”压缩成三个可比较路径。先看每条路径需要接受什么，再决定选哪一条。`;
  if (scene === "action") return pkg.decision ? "你已经作出方案选择。现在只需要确认负责人、启动条件，以及什么变化会触发重新判断。" : "行动页还在等待你的方案选择。";
  if (scene === "observe") return "我会持续观察结果、客户选择、关键驱动和风险护栏。没有真实变化时，不会要求你进行形式化复盘。";
  return `我已完成首轮梳理。目前最值得优先验证的是“${pkg.diagnosis.primary_breakpoint}”，但这仍取决于一个关键前提：${pkg.diagnosis.weakest_assumption}`;
}

function money(value, currency = "CNY") { return new Intl.NumberFormat("zh-CN", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(value ?? 0)); }
function translatePriority(value) { return ({ QUALITY: "客户选择质量", EFFICIENCY: "客户选择效率", COST: "客户选择成本" })[value] ?? value; }
function translateRobustness(value) { return ({ STABLE: "相对稳定", CONDITIONAL: "有条件成立", UNSTABLE: "仍不稳定", NOT_RUN: "等待评估" })[value] ?? value; }
function translateOptionType(value) { return ({ CONSERVATIVE: "快速验证", STANDARD: "优先方案", AMBITIOUS: "进取方案", NO_ACTION: "不行动基线" })[value] ?? value; }
function translateCeiling(value) { return ({ CLIENT_PREVIEW_UNTRUSTED: "客户首检预览，未放行", MAPPING_ONLY: "仅完成指标映射", RECORD_ONLY: "仅记录", CHANNEL_EXECUTION: "渠道执行", SAMPLE_BEHAVIOR: "样本行为", POPULATION_ESTIMATE: "总体估计", CAUSAL_EFFECT: "因果效应" })[value] ?? value; }
function translateQuantification(value) { return ({ DATA_FIRST: "先补关键数据", MEASURABLE: "可以正式观察", VALIDATION_ELIGIBLE: "可以进入验证" })[value] ?? "等待量化"; }
function translateDecisionState(value) { return ({ ACCEPTED_AS_IS: "已采纳，等待真实执行", REVISION_REQUESTED: "已要求修改", DEFERRED: "已延后", REJECTED: "已拒绝" })[value] ?? value; }
function translateEvent(value) { return ({ STARTED: "行动已开始", PAUSED: "行动已暂停", RESUMED: "行动已恢复", COMPLETED: "行动已完成", STOPPED: "行动已停止", SCOPE_CHANGED: "行动范围已变化" })[value] ?? value; }
function translateTrigger(value) { return ({ MATERIAL_DATA_CHANGE: "数据显著变化", EVIDENCE_CONFLICT: "证据发生冲突", ACTION_STATE_CHANGE: "行动状态变化", EVIDENCE_NEAR_MATURITY: "证据接近成熟", CUSTOMER_REQUESTED_CHECK: "你主动请求复核" })[value] ?? value; }
